import { useTranslation } from "react-i18next";
import { BreadcrumbJsonLd } from "@/src/common/components/breadcrumb-jsonld";
import { JsonLd } from "@/src/common/components/json-ld";
import { PageHelmet } from "@/src/common/components/page-helmet";
import { env } from "@/src/common/constants/env";
import { DocumentPageShell } from "../components/document-page-shell";
import { LEGAL_INFORMATION } from "../constants/legal";
import { MARKETING_ROUTES } from "../constants/marketing-routes";

function LegalSection({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<section className="rounded-4xl border border-white/10 bg-white/5 p-6 shadow-[0_24px_70px_-48px_rgba(0,0,0,0.78)] backdrop-blur sm:p-8">
			<h2 className="text-2xl font-semibold tracking-[-0.04em] text-white">{title}</h2>
			<div className="mt-5 space-y-3 text-sm leading-7 text-white/72">{children}</div>
		</section>
	);
}

export default function LegalNoticePage() {
	const { t } = useTranslation("marketing");
	const pageUrl = `${env.SITE_URL}${MARKETING_ROUTES.LEGAL_NOTICE}`;

	return (
		<>
			<PageHelmet title="Mentions légales" description="Mentions légales du site et du service LookDat." />
			<BreadcrumbJsonLd
				items={[
					{ name: t("breadcrumb_home"), url: env.SITE_URL },
					{ name: t("nav_legal"), url: pageUrl },
				]}
			/>
			<JsonLd
				data={{
					"@context": "https://schema.org",
					"@type": "WebPage",
					name: "Mentions légales",
					url: pageUrl,
					inLanguage: "fr",
					isPartOf: { "@id": `${env.SITE_URL}/#website` },
				}}
			/>
			<DocumentPageShell
				eyebrow="Mentions légales"
				title="Mentions légales"
				description="Tu trouveras ici les informations légales relatives à l'éditeur du service LookDat et à l'hébergement du site."
			>
				<LegalSection title="Éditeur du site">
					<p>
						Le site et le service <strong>{LEGAL_INFORMATION.brandName}</strong> sont édités par{" "}
						<strong>{LEGAL_INFORMATION.publisherName}</strong>,{` `}
						<strong>{LEGAL_INFORMATION.legalForm.toLowerCase()}</strong>, dont le siège social est situé au{` `}
						<strong>{LEGAL_INFORMATION.address}</strong>, immatriculé sous le numéro <strong>{LEGAL_INFORMATION.siren}</strong>.
					</p>
					<p>
						Adresse de contact : <strong>{LEGAL_INFORMATION.legalEmail}</strong>
					</p>
				</LegalSection>

				<LegalSection title="Directeur de la publication">
					<p>
						Le directeur de la publication est <strong>{LEGAL_INFORMATION.publicationDirector}</strong>.
					</p>
				</LegalSection>

				<LegalSection title="Hébergement">
					<p>
						Le site est hébergé par <strong>{LEGAL_INFORMATION.hostName}</strong>, situé au <strong>{LEGAL_INFORMATION.hostAddress}</strong>
						.
					</p>
				</LegalSection>

				<LegalSection title="Contact">
					<p>
						Pour toute demande relative au service, à la protection des données ou à l'utilisation du site, tu peux écrire à{` `}
						<strong>{LEGAL_INFORMATION.contactEmail}</strong>.
					</p>
				</LegalSection>
			</DocumentPageShell>
		</>
	);
}
